import { Controller, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useConversationStoreShallow } from 'src/store/conversationStore';
import * as yup from 'yup';

import { DateFormat } from '@speeki/frontend-utils';
import {
    Button,
    DateField,
    SelectChatField,
} from '@speeki/global-ui-components';

import { ArrElement } from '@@types/CommonTypes';

import {
    ConversationAssistantResponseFragment,
    Legacy_Slot_Type,
    SendReportMessageDocument,
    SlotResponseInput,
} from '@graphql/generated/graphql';
import { assistantResponse, isValidStringDate } from '@graphql/helpers';

import { countriesOptions, DictionaryOptionalSelect } from '@utils/dictionary';

import { useMutation } from '@apollo/client';
import { currentLanguageVar } from '@boot/reactiveVariables';
import {
    BubbleInput,
    BubblesWrapper,
    DateFieldWrapper,
    FormElementsWrapper,
    InputsWrapper,
    MessageInput,
    MessageTextarea,
    SelectWrapper,
    SendButtonWrapper,
    TextareaWrapper,
} from '@domains/new-conversation/organisms/ReportConversationForm/ReportConversationForm.styled';
import { yupResolver } from '@hookform/resolvers/yup';

export const useReportConversationForm = (caseUuid: string) => {
    const { t } = useTranslation();

    const {
        addConversationMessage,
        deleteConversationMessage,
        setConversationLoading,
    } = useConversationStoreShallow([
        'addConversationMessage',
        'setConversationLoading',
        'deleteConversationMessage',
    ]);

    const availableOptions = assistantResponse(caseUuid);
    const slotOptions = !!availableOptions?.slotOptions?.length;

    const [sendReportMessage, { loading }] = useMutation(
        SendReportMessageDocument,
        {
            context: {
                notifyOnError: true,
            },
            notifyOnNetworkStatusChange: true,
            onCompleted: (data) => {
                addConversationMessage({
                    ...data.conversation,
                    type: 'ASSISTANT',
                });
                reset();
                setConversationLoading(false);
            },
            onError: () => {
                deleteConversationMessage();
                setConversationLoading(false);
            },
        },
    );

    const slotFilter = (slotType: Legacy_Slot_Type) => {
        return availableOptions?.slotOptions?.filter(
            (element) => element.type === slotType,
        );
    };

    const {
        control,
        formState: { errors, isDirty, isSubmitting, isValid },
        handleSubmit,
        register,
        reset,
        watch,
    } = useForm<ReportConversationFormValues>({
        mode: 'onChange',
        // @ts-expect-error FIXME: REPAIR TYPES PROBLEM
        resolver: !slotOptions ? yupResolver(TextMessageSchema) : undefined,
        shouldUnregister: true,
    });

    const calendarField = useController({
        control,
        name: 'text',
    });

    const onSubmit = (
        formData: ReportConversationFormValues,
        slotID: number | null,
        actionID: number | null,
    ) => {
        const mergedValues = Object.entries(formData)
            .sort()
            .filter(([, value]) => value)
            .map(([, value]) => {
                if (typeof value === 'object')
                    return (value as DictionaryOptionalSelect).label;
                return value;
            })
            .join(', ')
            .replace(/(\r\n|\n|\r)/gm, ' ')
            .trim();

        const formKeys = Object.keys(formData)
            .filter((element) => {
                return !element.includes('BUBBLE');
            })
            .sort();

        const slots: SlotResponseInput[] | null = [];

        if (slotID) {
            availableOptions?.slotOptions?.forEach(
                ({ actionId, extraType, type }, index) => {
                    if (type === 'TEXT' && extraType === 'COUNTRY_SELECTOR') {
                        if (formData[formKeys[index]])
                            slots.push({
                                actionId: actionId,
                                text:
                                    (
                                        formData[
                                            formKeys[index]
                                        ] as DictionaryOptionalSelect
                                    ).value || '',
                            });
                    } else if (
                        (type === 'TEXT' && extraType === 'TEXT') ||
                        type === 'CALENDAR'
                    ) {
                        if (formData[formKeys[index]])
                            slots.push({
                                actionId: actionId,
                                text: formData[formKeys[index]] || '',
                            });
                    }
                },
            );
        }

        setConversationLoading(true);
        addConversationMessage({
            id: caseUuid,
            actionId: actionID || null,
            slotId: slotID || null,
            text: mergedValues,
            type: 'REPORTER',
        });

        void sendReportMessage({
            variables: {
                input: {
                    actionId: actionID || null,
                    caseUuid,
                    slotId: slotID || null,
                    slots: slots || null,
                    text: mergedValues,
                },
                targetLanguage: currentLanguageVar(),
            },
        });
    };

    const fieldTypes = (
        element: ArrElement<
            ConversationAssistantResponseFragment['slotOptions']
        >,
        name: string,
        slotID: number | null,
    ) => {
        return {
            BUBBLE: (
                <BubbleInput
                    key={name}
                    onClick={() => {
                        setConversationLoading(true);
                        addConversationMessage({
                            id: caseUuid,
                            actionId: element.actionId,
                            slotId: slotID,
                            text: element.text,
                            type: 'REPORTER',
                        });

                        void sendReportMessage({
                            variables: {
                                input: {
                                    actionId: element.actionId,
                                    caseUuid,
                                    slotId: slotID,
                                    slots: [
                                        {
                                            actionId: element.actionId,
                                            text: element.text,
                                        },
                                    ],
                                    text: element.text,
                                },
                                targetLanguage: currentLanguageVar(),
                            },
                        });
                    }}
                    size={'small'}
                    testId={`button_bubble-${slotID}-${element.actionId}`}
                    variant={'radio'}
                >
                    {element.text}
                </BubbleInput>
            ),
            CALENDAR: (
                <DateFieldWrapper>
                    <DateField
                        {...calendarField.field}
                        datePickerPlacement={'top'}
                        key={name}
                        onChange={(date) => {
                            calendarField.field.onChange(
                                DateFormat.standard(date as Date),
                            );
                        }}
                        testId="9TqVx6o"
                        placeholder={element.text}
                        //@ts-expect-error must be undefined before first apply
                        value={
                            isValidStringDate(calendarField.field.value)
                                ? new Date(calendarField.field.value)
                                : undefined
                        }
                    />
                </DateFieldWrapper>
            ),
            MAP: null,
            NONE: null,
            TEXT:
                element.extraType === 'COUNTRY_SELECTOR' ? (
                    <SelectWrapper key={name}>
                        <Controller
                            control={control}
                            name={`${name}`}
                            render={({ field }) => (
                                <SelectChatField
                                    {...field}
                                    maxMenuHeight={120}
                                    options={countriesOptions()}
                                    placeholder={element.text}
                                    testId="3TH7npm"
                                />
                            )}
                        />
                    </SelectWrapper>
                ) : (
                    <MessageInput
                        {...register(name, { maxLength: 500 })}
                        defaultValue={''}
                        key={name}
                        placeholder={element.text}
                    />
                ),
        };
    };

    const sendButton = (slotID: number | null, actionID: number | null) => {
        const currentFormValues = Object.values(watch())
            .filter((element) => element)
            .join(', ')
            .replace(/(\r\n|\n|\r)/gm, ' ')
            .trim();

        return (
            <SendButtonWrapper isError={!!errors.text}>
                <Button
                    disabled={
                        !isDirty ||
                        isSubmitting ||
                        !currentFormValues ||
                        !isValid
                    }
                    iconProps={{
                        icon: 'SvgSend24',
                        size: 24,
                    }}
                    onClick={handleSubmit((formData) => {
                        onSubmit(formData, slotID, actionID);
                    })}
                    testId="button_send"
                    variant={'primary'}
                />
            </SendButtonWrapper>
        );
    };

    const groupSlotOptions = () => {
        if (slotOptions) {
            const textArray = slotFilter('TEXT');
            const bubbleArray = slotFilter('BUBBLE');
            const calendarArray = slotFilter('CALENDAR');
            const slotId = availableOptions?.nextSlotId ?? null;

            if (
                !!textArray?.length &&
                !!bubbleArray?.length &&
                !!calendarArray?.length
            )
                return null;

            return (
                <>
                    {!!bubbleArray?.length && (
                        <BubblesWrapper>
                            {bubbleArray.map(
                                (element, actionIndex) =>
                                    fieldTypes(
                                        element,
                                        `${element.type}-${actionIndex}`,
                                        slotId,
                                    )[element.type],
                            )}
                        </BubblesWrapper>
                    )}

                    {(!!textArray?.length || !!calendarArray?.length) && (
                        <FormElementsWrapper>
                            <InputsWrapper>
                                {textArray?.map(
                                    (element, actionIndex) =>
                                        fieldTypes(
                                            element,
                                            `${element.type}-${actionIndex}`,
                                            slotId,
                                        )[element.type],
                                )}
                                {calendarArray?.map(
                                    (element, actionIndex) =>
                                        fieldTypes(
                                            element,
                                            `${element.type}-${actionIndex}`,
                                            slotId,
                                        )[element.type],
                                )}
                            </InputsWrapper>
                            {sendButton(
                                slotId,
                                textArray?.[0]?.actionId ||
                                    calendarArray?.[0]?.actionId ||
                                    null,
                            )}
                        </FormElementsWrapper>
                    )}
                </>
            );
        }

        return (
            <TextareaWrapper>
                <MessageTextarea
                    className="htmlToImageIgnore"
                    defaultValue={''}
                    error={!!errors.text}
                    message={t(errors.text?.message || '')}
                    placeholder={t('CHAT_YOUR_MESSAGE')}
                    testId="Wktyd8O"
                    {...register('text')}
                />
                {sendButton(null, 0)}
            </TextareaWrapper>
        );
    };

    return {
        groupSlotOptions,
        loading,
    };
};

const TextMessageSchema = yup.object({
    text: yup
        .string()
        .required('FIELD_CANNOT_BE_EMPTY')
        .max(800, 'CHAT_MAX_CHARS'),
});

type ReportConversationFormValues = Record<string, string>;
